import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Eye, Save, Mail, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { adminApi } from '@/services/adminApi';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { EMAIL_TEMPLATES, renderTemplate } from '@/lib/emailTemplates';
import type { TemplateParams } from '@/lib/emailTemplates';

interface CampaignContent {
  templateId: string;
  heading: string;
  body: string;
  ctaText: string;
  ctaUrl: string;
}

// Encode structured content into a JSON comment at the start of the HTML
// so we can recover the fields when loading an existing campaign
function encodeMetadata(content: CampaignContent): string {
  return `<!-- BVA_TEMPLATE:${JSON.stringify({
    t: content.templateId,
    h: content.heading,
    b: content.body,
    ct: content.ctaText,
    cu: content.ctaUrl,
  })} -->`;
}

function decodeMetadata(html: string): CampaignContent | null {
  const match = html.match(/<!-- BVA_TEMPLATE:(.*?) -->/);
  if (!match) return null;
  try {
    const data = JSON.parse(match[1]);
    return {
      templateId: data.t || 'newsletter',
      heading: data.h || '',
      body: data.b || '',
      ctaText: data.ct || '',
      ctaUrl: data.cu || '',
    };
  } catch {
    return null;
  }
}

export function CampaignEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSendTestModal, setShowSendTestModal] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('austin@bankvaultacademy.org');
  const [sendingTest, setSendingTest] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientList, setRecipientList] = useState<Array<{ leadId: string; email: string; firstName: string | null; lastName: string | null }>>([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState('draft');

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [targetLeadTypes, setTargetLeadTypes] = useState('');
  const [targetLeadSources, setTargetLeadSources] = useState('');

  const [content, setContent] = useState<CampaignContent>({
    templateId: 'newsletter',
    heading: '',
    body: '',
    ctaText: '',
    ctaUrl: '',
  });

  // Legacy mode for campaigns created with raw HTML
  const [isLegacy, setIsLegacy] = useState(false);
  const [legacyHtml, setLegacyHtml] = useState('');
  const [legacyPlainText, setLegacyPlainText] = useState('');

  useEffect(() => {
    if (isEditing) {
      loadCampaign();
    }
  }, [id]);

  const loadCampaign = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getCampaign(id!);
      setName(data.name);
      setSubject(data.subject);
      setTargetLeadTypes(data.targetLeadTypes || '');
      setTargetLeadSources(data.targetLeadSources || '');
      setCampaignStatus(data.status);

      // Try to recover structured content from metadata
      const meta = decodeMetadata(data.htmlContent);
      if (meta) {
        setContent(meta);
      } else {
        // Legacy campaign with raw HTML
        setIsLegacy(true);
        setLegacyHtml(data.htmlContent);
        setLegacyPlainText(data.plainTextContent);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to load campaign');
      navigate('/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const templateParams: TemplateParams = useMemo(() => ({
    heading: content.heading || 'Your Heading Here',
    body: content.body || 'Your message content goes here...',
    ctaText: content.ctaText || undefined,
    ctaUrl: content.ctaUrl || undefined,
  }), [content]);

  const rendered = useMemo(() => {
    if (isLegacy) {
      return { html: legacyHtml, plainText: legacyPlainText };
    }
    try {
      return renderTemplate(content.templateId, templateParams);
    } catch {
      return { html: '', plainText: '' };
    }
  }, [content.templateId, templateParams, isLegacy, legacyHtml, legacyPlainText]);

  const getHtmlForSave = (): string => {
    if (isLegacy) return legacyHtml;
    // Prepend metadata comment for future editing
    return encodeMetadata(content) + '\n' + rendered.html;
  };

  const getPlainTextForSave = (): string => {
    if (isLegacy) return legacyPlainText;
    return rendered.plainText;
  };

  const handleSave = async () => {
    if (!name.trim() || !subject.trim()) {
      alert('Campaign name and subject are required');
      return;
    }
    if (!isLegacy && !content.heading.trim()) {
      alert('Email heading is required');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name,
        subject,
        htmlContent: getHtmlForSave(),
        plainTextContent: getPlainTextForSave(),
        targetLeadTypes: targetLeadTypes || undefined,
        targetLeadSources: targetLeadSources || undefined,
      };

      if (isEditing) {
        await adminApi.updateCampaign(id!, payload);
      } else {
        const result = await adminApi.createCampaign(payload);
        // Navigate to edit mode so send/preview become available
        navigate(`/campaigns/${result.campaignId}/edit`, { replace: true });
        return;
      }

      navigate('/campaigns');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      alert('Please enter an email address');
      return;
    }

    try {
      setSendingTest(true);

      // Auto-save first
      const payload = {
        name,
        subject,
        htmlContent: getHtmlForSave(),
        plainTextContent: getPlainTextForSave(),
        targetLeadTypes: targetLeadTypes || undefined,
        targetLeadSources: targetLeadSources || undefined,
      };
      await adminApi.updateCampaign(id!, payload);

      // Send preview
      await adminApi.previewCampaign(id!, testEmail);
      alert(`Test email sent to ${testEmail}!`);
      setShowSendTestModal(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleOpenSendDialog = async () => {
    try {
      setLoadingRecipients(true);

      // Auto-save first
      const payload = {
        name,
        subject,
        htmlContent: getHtmlForSave(),
        plainTextContent: getPlainTextForSave(),
        targetLeadTypes: targetLeadTypes || undefined,
        targetLeadSources: targetLeadSources || undefined,
      };
      await adminApi.updateCampaign(id!, payload);

      // Fetch matching recipients
      const data = await adminApi.getCampaignRecipients(id!);
      setRecipientList(data.recipients);
      setSelectedLeadIds(new Set(data.recipients.map(r => r.leadId)));
      setShowSendDialog(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleConfirmSend = async () => {
    try {
      setSending(true);
      const isManual = targetLeadSources === 'manual';
      const result = await adminApi.sendCampaign(
        id!,
        isManual ? Array.from(selectedLeadIds) : undefined,
      );
      alert(`Campaign sent to ${result.totalSent} recipients!`);
      navigate('/campaigns');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!confirm('Are you sure you want to delete this campaign? This cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      await adminApi.deleteCampaign(id!);
      navigate('/campaigns');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete campaign');
    } finally {
      setDeleting(false);
    }
  };

  const toggleLeadSelection = (leadId: string) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedLeadIds.size === recipientList.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(recipientList.map(r => r.leadId)));
    }
  };

  const updateContent = (updates: Partial<CampaignContent>) => {
    setContent(prev => ({ ...prev, ...updates }));
  };

  if (loading) {
    return <LoadingSpinner message="Loading campaign..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/campaigns')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {isEditing ? 'Edit Campaign' : 'New Campaign'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEditing ? 'Update your email campaign' : 'Create a new email campaign'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing && (
            <>
              {(campaignStatus === 'draft' || campaignStatus === 'cancelled') && (
                <Button variant="destructive" onClick={handleDeleteCampaign} disabled={deleting}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowSendTestModal(true)}>
                <Mail className="w-4 h-4 mr-2" />
                Send Test
              </Button>
              <Button variant="outline" onClick={() => setShowPreviewModal(true)}>
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button onClick={handleOpenSendDialog} disabled={sending || loadingRecipients}>
                <Send className="w-4 h-4 mr-2" />
                {loadingRecipients ? 'Loading...' : 'Send Campaign'}
              </Button>
            </>
          )}
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content — Left 2/3 */}
        <div className="lg:col-span-2 space-y-6">
          {/* Campaign Details */}
          <Card>
            <CardHeader>
              <CardTitle>Campaign Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Campaign Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., March 2026 Newsletter"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Email Subject *</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., Financial Literacy Resources for Your Family"
                />
              </div>
            </CardContent>
          </Card>

          {/* Template Picker */}
          {!isLegacy && (
            <Card>
              <CardHeader>
                <CardTitle>Choose Template</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {EMAIL_TEMPLATES.map((tmpl) => (
                    <button
                      key={tmpl.id}
                      type="button"
                      onClick={() => updateContent({ templateId: tmpl.id })}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        content.templateId === tmpl.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      <p className="font-medium text-sm text-foreground">{tmpl.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{tmpl.description}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Content Fields */}
          <Card>
            <CardHeader>
              <CardTitle>Email Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLegacy ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="legacyHtml">HTML Content</Label>
                    <Textarea
                      id="legacyHtml"
                      value={legacyHtml}
                      onChange={(e) => setLegacyHtml(e.target.value)}
                      placeholder="HTML email content..."
                      className="min-h-[300px] font-mono text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="legacyPlainText">Plain Text Content</Label>
                    <Textarea
                      id="legacyPlainText"
                      value={legacyPlainText}
                      onChange={(e) => setLegacyPlainText(e.target.value)}
                      placeholder="Plain text version..."
                      className="min-h-[150px]"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="heading">Heading *</Label>
                    <Input
                      id="heading"
                      value={content.heading}
                      onChange={(e) => updateContent({ heading: e.target.value })}
                      placeholder="e.g., New Resources Available"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="body">Body</Label>
                    <Textarea
                      id="body"
                      value={content.body}
                      onChange={(e) => updateContent({ body: e.target.value })}
                      placeholder="Write your email content here. Use blank lines to separate paragraphs.&#10;&#10;The merge tag {{firstName}} will be replaced with each recipient's first name."
                      className="min-h-[200px]"
                    />
                    <p className="text-xs text-muted-foreground">
                      Separate paragraphs with blank lines. Use {'{{firstName}}'} for personalization.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ctaText">Button Text</Label>
                      <Input
                        id="ctaText"
                        value={content.ctaText}
                        onChange={(e) => updateContent({ ctaText: e.target.value })}
                        placeholder="e.g., Learn More"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ctaUrl">Button URL</Label>
                      <Input
                        id="ctaUrl"
                        value={content.ctaUrl}
                        onChange={(e) => updateContent({ ctaUrl: e.target.value })}
                        placeholder="https://bankvaultacademy.org"
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar — Right 1/3 */}
        <div className="space-y-6">
          {/* Target Audience */}
          <Card>
            <CardHeader>
              <CardTitle>Target Audience</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="targetLeadTypes">Lead Types</Label>
                <select
                  id="targetLeadTypes"
                  value={targetLeadTypes}
                  onChange={(e) => setTargetLeadTypes(e.target.value)}
                  className="form-input w-full"
                >
                  <option value="">All Lead Types</option>
                  <option value="teen">Teen</option>
                  <option value="school">Schools Only</option>
                  <option value="coop">Co-ops Only</option>
                  <option value="general">General Only</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="targetLeadSources">Lead Sources</Label>
                <select
                  id="targetLeadSources"
                  value={targetLeadSources}
                  onChange={(e) => setTargetLeadSources(e.target.value)}
                  className="form-input w-full"
                >
                  <option value="">All Sources</option>
                  <option value="simulator">Simulator</option>
                  <option value="lead_magnet">Lead Magnet</option>
                  <option value="school_prospect">School Prospect</option>
                  <option value="coop_inquiry">Co-op Inquiry</option>
                  <option value="manual">Manual</option>
                </select>
              </div>

              <p className="text-xs text-muted-foreground">
                Only subscribed leads matching these criteria will receive the email.
              </p>
            </CardContent>
          </Card>

          {/* Live Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Live Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className="border rounded-lg overflow-hidden bg-white"
                style={{ height: '400px' }}
              >
                <iframe
                  srcDoc={rendered.html.replace(/\{\{firstName\}\}/g, 'Austin')}
                  title="Email Preview"
                  className="w-full h-full border-0"
                  sandbox=""
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Preview shows how the email will look in most email clients.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Full Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto" style={{ maxHeight: 'calc(90vh - 120px)' }}>
            <div className="border rounded-lg overflow-hidden bg-white">
              <iframe
                srcDoc={rendered.html.replace(/\{\{firstName\}\}/g, 'Austin')}
                title="Email Preview"
                className="w-full border-0"
                style={{ height: '600px' }}
                sandbox=""
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Recipients Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              Review Recipients ({targetLeadSources === 'manual' ? selectedLeadIds.size : recipientList.length})
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {recipientList.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No matching subscribed leads found for this campaign's targeting criteria.
              </p>
            ) : (
              <>
                {targetLeadSources === 'manual' && (
                  <div className="flex items-center gap-2 mb-3 pb-3 border-b">
                    <input
                      type="checkbox"
                      checked={selectedLeadIds.size === recipientList.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-muted-foreground">
                      {selectedLeadIds.size === recipientList.length ? 'Deselect all' : 'Select all'}
                    </span>
                  </div>
                )}
                <div className="overflow-auto" style={{ maxHeight: '400px' }}>
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-background border-b">
                      <tr>
                        {targetLeadSources === 'manual' && <th className="text-left p-2 w-8"></th>}
                        <th className="text-left p-2">Email</th>
                        <th className="text-left p-2">First Name</th>
                        <th className="text-left p-2">Last Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipientList.map(r => (
                        <tr key={r.leadId} className="border-b last:border-0 hover:bg-muted/50">
                          {targetLeadSources === 'manual' && (
                            <td className="p-2">
                              <input
                                type="checkbox"
                                checked={selectedLeadIds.has(r.leadId)}
                                onChange={() => toggleLeadSelection(r.leadId)}
                                className="w-4 h-4 rounded border-gray-300"
                              />
                            </td>
                          )}
                          <td className="p-2">{r.email}</td>
                          <td className="p-2">{r.firstName || '—'}</td>
                          <td className="p-2">{r.lastName || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSend}
              disabled={sending || (targetLeadSources === 'manual' && selectedLeadIds.size === 0)}
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sending...' : `Send to ${targetLeadSources === 'manual' ? selectedLeadIds.size : recipientList.length} Recipients`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Test Email Modal */}
      <Dialog open={showSendTestModal} onOpenChange={setShowSendTestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Send a test version of this email to verify it looks correct before sending to all recipients.
              The campaign will be auto-saved first.
            </p>
            <div className="space-y-2">
              <Label htmlFor="testEmail">Send to:</Label>
              <Input
                id="testEmail"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="austin@bankvaultacademy.org"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendTestModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendTest} disabled={sendingTest}>
              <Mail className="w-4 h-4 mr-2" />
              {sendingTest ? 'Sending...' : 'Send Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
